import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Eye, EyeOff, AlertTriangle } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import LocationShare from "../Location/LocationShare";
import "./Auth.css";

const Register = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    confirmPassword: "",
    role: "restaurant",
    phone: "",
    address: "",
    city: "",
    postal_code: "",
    description: "",
    certification: "",
    bank_rib: "",
    mobile_money: "",
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (formData.password !== formData.confirmPassword) {
      setError("Les mots de passe ne correspondent pas");
      return;
    }

    if (formData.password.length < 6) {
      setError("Le mot de passe doit contenir au moins 6 caractères");
      return;
    }

    setLoading(true);

    const { confirmPassword, ...registerData } = formData;
    const result = await register(registerData);
    
    if (result.success) {
      navigate("/");
    } else {
      setError(result.error);
    }
    
    setLoading(false);
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="auth-header">
          <h1>Inscription</h1>
          <p>Rejoignez PouletDirect et connectez-vous avec des professionnels</p>
        </div>

        {error && <div className="alert alert-error">{error}</div>}

        <form onSubmit={handleSubmit} className="auth-form">
          <div className="form-group">
            <label htmlFor="name" className="form-label">
              Nom / Nom de l'établissement <span style={{color: 'red'}}>*</span>
            </label>
            <input
              type="text"
              id="name"
              name="name"
              className="form-control"
              value={formData.name}
              onChange={handleChange}
              required
              placeholder="Votre nom"
            />
          </div>

          <div className="form-group">
            <label htmlFor="email" className="form-label">
              Email
            </label>
            <input
              type="email"
              id="email"
              name="email"
              className="form-control"
              value={formData.email}
              onChange={handleChange}
              placeholder="votre@email.com"
            />
          </div>

          <div className="form-group">
            <label htmlFor="role" className="form-label">
              Je suis <span style={{color: 'red'}}>*</span>
            </label>
            <select
              id="role"
              name="role"
              className="form-control"
              value={formData.role}
              onChange={handleChange}
              required
            >
              <option value="restaurant">Restaurateur</option>
              <option value="breeder">Éleveur</option>
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="password" className="form-label">
              Mot de passe <span style={{color: 'red'}}>*</span>
            </label>
            <div className="password-input-wrapper">
              <input
                type={showPassword ? "text" : "password"}
                id="password"
                name="password"
                className="form-control password-field"
                value={formData.password}
                onChange={handleChange}
                required
                minLength={6}
                placeholder="••••••••"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="password-toggle-btn"
                aria-label={showPassword ? "Masquer le mot de passe" : "Afficher le mot de passe"}
              >
                {showPassword ? <EyeOff size={18} strokeWidth={2.25} /> : <Eye size={18} strokeWidth={2.25} />}
              </button>
            </div>
            <small style={{ color: formData.password && formData.password.length < 6 ? "var(--error)" : "#666", fontSize: "0.85rem", marginTop: "0.25rem", display: "block" }}>
              Au moins 6 caractères.
            </small>
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword" className="form-label">
              Confirmer le mot de passe <span style={{color: 'red'}}>*</span>
            </label>
            <div className="password-input-wrapper">
              <input
                type={showConfirmPassword ? "text" : "password"}
                id="confirmPassword"
                name="confirmPassword"
                className="form-control password-field"
                value={formData.confirmPassword}
                onChange={handleChange}
                required
                placeholder="••••••••"
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="password-toggle-btn"
                aria-label={showConfirmPassword ? "Masquer le mot de passe" : "Afficher le mot de passe"}
              >
                {showConfirmPassword ? <EyeOff size={18} strokeWidth={2.25} /> : <Eye size={18} strokeWidth={2.25} />}
              </button>
            </div>
            {formData.confirmPassword && (
              <small style={{ color: formData.password === formData.confirmPassword ? "var(--success)" : "var(--error)", fontSize: "0.85rem", marginTop: "0.25rem", display: "block" }}>
                {formData.password === formData.confirmPassword ? "Les mots de passe correspondent." : "Les mots de passe ne correspondent pas."}
              </small>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="phone" className="form-label">
              Téléphone <span style={{color: 'red'}}>*</span>
            </label>
            <input
              type="tel"
              id="phone"
              name="phone"
              className="form-control"
              value={formData.phone}
              onChange={handleChange}
              required
              placeholder="+226 70 70 70 70"
            />
          </div>

          <LocationShare
            value={formData.address}
            onChange={handleChange}
            placeholder="Adresse complète"
            label="Adresse"
            required={true}
            name="address"
          />

          <div className="form-group">
            <label htmlFor="city" className="form-label">
              Ville <span style={{color: 'red'}}>*</span>
            </label>
            <input
              type="text"
              id="city"
              name="city"
              className="form-control"
              value={formData.city}
              onChange={handleChange}
              required
              placeholder="Ouagadougou"
            />
          </div>

          {formData.role === "breeder" && (
            <>
              <div className="form-group">
                <label htmlFor="certification" className="form-label">
                  Certifications (optionnel)
                </label>
                <input
                  type="text"
                  id="certification"
                  name="certification"
                  className="form-control"
                  value={formData.certification}
                  onChange={handleChange}
                  placeholder="Bio, Label Rouge, etc."
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="mobile_money" className="form-label">
                  Numéro Mobile Money <span style={{color: 'red'}}>*</span>
                </label>
                <input
                  type="tel"
                  id="mobile_money"
                  name="mobile_money"
                  className="form-control"
                  value={formData.mobile_money}
                  onChange={handleChange}
                  required
                  placeholder="+226 70 70 70 70"
                />
                <small className="icon-inline" style={{color: '#666', fontSize: '0.85rem', marginTop: '0.25rem', display: 'flex', alignItems: 'flex-start'}}>
                  <AlertTriangle size={13} strokeWidth={2.25} style={{flexShrink: 0, marginTop: '0.15rem'}} /> Important : Ce numéro est nécessaire pour recevoir les paiements de la plateforme. Les fonds vous seront versés sur ce compte mobile money lorsque le paiement sera libéré.
                </small>
              </div>
              
              <div className="form-group">
                <label htmlFor="bank_rib" className="form-label">
                  RIB (Relevé d'Identité Bancaire) - Optionnel
                </label>
                <input
                  type="text"
                  id="bank_rib"
                  name="bank_rib"
                  className="form-control"
                  value={formData.bank_rib}
                  onChange={handleChange}
                  placeholder="Code banque - Code guichet - Numéro de compte - Clé RIB"
                />
                <small style={{color: '#666', fontSize: '0.85rem', marginTop: '0.25rem', display: 'block'}}>
                  Format : 12345 67890 12345678901 12 (pour les virements bancaires)
                </small>
              </div>
            </>
          )}

          <button
            type="submit"
            className="btn btn-primary btn-lg"
            disabled={loading}
            style={{ width: "100%" }}
          >
            {loading ? "Inscription..." : "S'inscrire"}
          </button>
        </form>

        <div className="auth-footer">
          <p>
            Déjà un compte ?{" "}
            <Link to="/login" className="auth-link">
              Connectez-vous
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Register;
